import { useEffect, useState } from "react";
import { AbsoluteCenter } from "@chakra-ui/react";

import { useFormSteps, useImageCompression } from "@/hooks";
import { DeviceOrientationEventiOS, FormSteps } from "@/types/data";
import RackPhoto from "../racks/RackObservation/RackPhoto";
import LiveCamera from "../racks/RackObservation/LiveCamera";
import CountResults from "./CountResults";
import { useCountFragments } from "@/hooks";
import { useDetectionToAnnotation } from "@/hooks";
import { Annotorious } from "@annotorious/react";


function Count() {
  const [image, setImage] = useState<File>();
  const { compressedImage } = useImageCompression(image);
  const [detections, setDetections] = useState([]);
  const annotations = useDetectionToAnnotation(detections);
  const { formStep, setFormStep } = useFormSteps();
  const { countFragments, isPending, error } = useCountFragments();


  const [orientationPermission, setOrientationPermission] = useState<
    "granted" | "denied" | undefined
  >(undefined);
  const [orientation, setOrientation] =
    useState<DeviceOrientationEventiOS | null>(null);
  const requestPermission = (
    DeviceOrientationEvent as unknown as DeviceOrientationEventiOS
  ).requestPermission;
  const iOS = typeof requestPermission === "function";

  // Non-iOS browsers (Android, desktop) fire deviceorientation without any
  // permission prompt, so auto-grant for them.
  useEffect(() => {
    if (!iOS && window.DeviceOrientationEvent) {
      setOrientationPermission("granted");
    }
  }, [iOS]);

  useEffect(() => {
    if (orientationPermission === "granted" && window.DeviceOrientationEvent) {
      const handleOrientation = (event: DeviceOrientationEvent) => {
        setOrientation({
          alpha: event.alpha,
          beta: event.beta,
          gamma: event.gamma,
          absolute: event.absolute,
        } as unknown as DeviceOrientationEventiOS);
      };

      window.addEventListener("deviceorientation", handleOrientation, true);
      return () => {
        window.removeEventListener(
          "deviceorientation",
          handleOrientation,
          true,
        );
      };
    }
  }, [orientationPermission]);

  useEffect(() => {
    if (compressedImage) {
      countFragments(
        compressedImage,
        {
          onSuccess: (res) => {
            if (res.results.length) setDetections(res.results[0].detections);
          }
        });
    }
  }, [compressedImage, countFragments]);


  return (
    <>
      {formStep === FormSteps.Photo && (
        <AbsoluteCenter>
          <RackPhoto
            onOpenCamera={async () => {
              setFormStep(FormSteps.LiveCamera);
              // prompt user to grant orientation permission on iOS
              if (iOS) {
                const response = await requestPermission();
                if (response === "granted") {
                  setOrientationPermission("granted");
                }
              }
            }}
            setImage={(f) => {
              setImage(f);
              setFormStep(FormSteps.ObservationForm);
            }}
          />
        </AbsoluteCenter>
      )}
      {formStep === FormSteps.LiveCamera && (
        <LiveCamera
          onCapture={(f) => {
            setImage(f);
            setFormStep(FormSteps.ObservationForm);
          }}
          onBack={() => setFormStep(FormSteps.Photo)}
          requestOrientationPermission={
            iOS && orientationPermission !== "granted"
          }
          orientationPermissionCallback={async () => {
            if (iOS) {
              const response = await requestPermission();
              if (response === "granted") {
                setOrientationPermission("granted");
              }
            }
          }}
          onUpload={(f) => {
            setImage(f);
            setFormStep(FormSteps.ObservationForm);
          }}
          orientation={orientation}
          countMode={true}
        />
      )}
      {formStep === FormSteps.ObservationForm && compressedImage && (
        <Annotorious>
          <CountResults image={compressedImage!} annotations={annotations} isLoading={isPending} error={error} />
        </Annotorious>
      )}
    </>
  );
}


export default Count;
